import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ForgotPasswordError } from "utilities/Constant";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { emailAddress } from "utilities/formSchemas";
import "./forgot-password.scss";
import { useEffect } from "react";
import { usePostForgotPasswordMutation } from "./forgotPasswordService";
import { LoadingOverlay } from "components/ui/loading-spinners";
import ThankYou from "pages/thank-you/ThankYou";
import Footer from "components/footer/Footer";
import { useRobotsMetaTag } from "hooks/useRobotsMetaTag";
import { FLOW_CONTEXT, trackFSAuthError } from "utilities/full-story";
const formSchema = z.object({ email: emailAddress });

type ForgotPasswordFormValues = z.infer<typeof formSchema>;

type ForgotPasswordView = "loading" | "success" | "form";

const getForgotPasswordView = (
  isLoading: boolean,
  isSuccess: boolean,
  data?: boolean
): ForgotPasswordView => {
  if (isLoading) {
    return "loading";
  }
  if (isSuccess && data === true) {
    return "success";
  }
  return "form";
};

const shouldShowForgotPasswordError = (isSuccess: boolean, isError: boolean, data?: boolean) =>
  isError || (isSuccess && data === false);

const ForgotPasswordErrorMessages = () => (
  <div className="error__container">
    {ForgotPasswordError.map((message) => (
      <p key={message} className="error__message">
        {message}
      </p>
    ))}
  </div>
);

export default function ForgotPassword() {
  useRobotsMetaTag("noindex,nofollow");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({ resolver: zodResolver(formSchema) });

  const [sendRequest, { data, isLoading, isSuccess, isError, error, reset }] = usePostForgotPasswordMutation();

  const view = getForgotPasswordView(isLoading, isSuccess, data);
  const showError = shouldShowForgotPasswordError(isSuccess, isError, data);

  useEffect(() => {
    if (isError) {
      trackFSAuthError(FLOW_CONTEXT.recover, error);
    }
  }, [isError, error]);

  const onSubmit = async ({ email }: ForgotPasswordFormValues) => {
    reset();
    await sendRequest({ username: email });
  };

  if (view === "loading") {
    return <LoadingOverlay text="Loading..." />;
  }

  if (view === "success") {
    return <ThankYou />;
  }

  return (
    <>
      <div className="forgot-password">
        <div className="forgot-password__wrapper">
          <p className="forgot-password__header">Reset password</p>
          <p className="forgot-password__text">Enter your email, and we’ll send you a link</p>
        </div>
        {showError && <ForgotPasswordErrorMessages />}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            formHandle={register("email")}
            inputProps={{
              name: "email",
              placeholder: "Enter email",
              label: "Email Address",
              errorMessage: errors.email?.message,
              excludeFromTranslation: false,
            }}
          />
          <Button title="Send reset link" />
        </Form>
      </div>
      <div className="forgot-password__footer">
        <Footer />
      </div>
    </>
  );
}
